import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, BookIcon, AlertCircleIcon, TabletIcon, HeadphonesIcon, BookOpenIcon, BookTextIcon, LayersIcon } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';
import { useAfterTideUser } from '../../context/AfterTideUserContext';
import { getLinkedStoryForBook } from '../../data/linkedEditions';
// Define types for our component props
interface Book {
  id: string;
  title: string;
  author: string;
  coverImage: string;
  rating?: number;
  reviewCount?: number;
  genres?: string[];
  genre?: string; // Support single genre from MyBookshelf
  subgenres?: string[];
  subgenre?: string; // Support single subgenre from MyBookshelf
  spiceLevel?: number;
  burnLevel?: number;
  darknessLevel?: number;
  endingType?: string;
  relationshipType?: string;
  contentWarnings?: string[];
  seriesName?: string;
  series?: string; // Support series from MyBookshelf
  seriesStatus?: string;
  seriesNumber?: number;
  totalSeriesBooks?: number;
  formats?: string[]; // Array of available formats
  isNew?: boolean;
  isDebut?: boolean;
  coAuthor?: string; // Added for collaborations
  consentFramework?: string; // AfterTide specific
  tropes?: string[];
  pairingType?: string;
  primaryGenre?: string;
  mainSubgenre?: string;
  secondaryGenres?: string[];
  subgenreType?: 'core' | 'crossover' | 'microgenre';
  displayLabel?: string;
  shortLabel?: string;
  refinements?: string[];
  isAnthology?: boolean;
}
interface Series {
  id: string;
  name: string;
  status: string; // 'ongoing', 'completed', 'hiatus'
  books: Book[];
  totalBooks: number;
  isNew?: boolean;
  isDebut?: boolean;
}
interface UnifiedBookCardProps {
  book?: Book;
  series?: Series;
  darkMode?: boolean;
  isCollaboration?: boolean; // Added for collaborations
  isSigned?: boolean; // Show "Signed" badge in hover overlay
}
// Helper function to get color for genre
const getGenreColor = (genre: string): string => {
  if (!genre || typeof genre !== 'string') return 'bg-violet-600';
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-indigo-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-600';
  if (genreLower.includes('young adult') || genreLower.includes('ya')) return 'bg-cyan-600';
  if (genreLower.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600'; // Default color
};
// Helper function to get color for series status
const getStatusColor = (status: string, isNew?: boolean, isDebut?: boolean): string => {
  // Priority: Debut > New > Series Status
  if (isDebut) return 'bg-fuchsia-600';
  if (isNew) return 'bg-indigo-500';
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'completed') return 'bg-green-600';
  if (statusLower === 'ongoing') return 'bg-yellow-500';
  if (statusLower.includes('hiatus') || statusLower.includes('discontinued') || statusLower.includes('stopped') || statusLower.includes('removed')) return 'bg-red-600';
  return 'bg-blue-600'; // Default color for unknown status
};
// Helper: series number badge color matches series status (for overlay X/Y badge)
const getSeriesBadgeColor = (status: string, isNew?: boolean, isDebut?: boolean): string => {
  if (isDebut) return 'bg-fuchsia-600/80';
  if (isNew) return 'bg-indigo-500/80';
  const s = status?.toLowerCase() || '';
  if (s === 'completed') return 'bg-green-600/80';
  if (s === 'ongoing') return 'bg-blue-500/80';
  if (s.includes('hiatus')) return 'bg-amber-500/80';
  if (s.includes('discontinued') || s.includes('stopped') || s.includes('removed')) return 'bg-red-600/80';
  return 'bg-indigo-500/80';
};
// Helper function to get color for content tags
const getContentTagColor = (tag: string): string => {
  if (!tag || typeof tag !== 'string') return 'bg-gray-500';
  const tagLower = tag.toLowerCase();
  if (tagLower.includes('slow burn') || tagLower.includes('enemies to lovers') || tagLower.includes('friends to lovers') || tagLower.includes('forbidden')) {
    return 'bg-pink-500'; // Relationship tropes
  }
  if (tagLower.includes('magic') || tagLower.includes('supernatural') || tagLower.includes('paranormal')) {
    return 'bg-indigo-500'; // Fantasy elements
  }
  if (tagLower.includes('suspense') || tagLower.includes('mystery') || tagLower.includes('thriller')) {
    return 'bg-indigo-500'; // Thriller elements
  }
  if (tagLower.includes('dark') || tagLower.includes('violence') || tagLower.includes('mature')) {
    return 'bg-gray-700'; // Dark content
  }
  return 'bg-indigo-500'; // Default color
};
const spiceLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Mild',
    color: 'bg-sky-500/30 text-sky-200'
  },
  2: {
    label: 'Warm',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Hot',
    color: 'bg-orange-500/30 text-orange-200'
  },
  4: {
    label: 'Steamy',
    color: 'bg-rose-500/30 text-rose-200'
  },
  5: {
    label: 'Scorching',
    color: 'bg-red-500/30 text-red-200'
  }
};
const burnLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Slow Burn',
    color: 'bg-cyan-500/30 text-cyan-200'
  },
  2: {
    label: 'Medium Burn',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Fast Burn',
    color: 'bg-orange-500/30 text-orange-200'
  }
};
const darknessLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Shadow',
    color: 'bg-slate-400/30 text-slate-200'
  },
  2: {
    label: 'Grey',
    color: 'bg-slate-500/30 text-slate-200'
  },
  3: {
    label: 'Abyss',
    color: 'bg-gray-600/30 text-gray-200'
  },
  4: {
    label: 'Pitch Black',
    color: 'bg-gray-700/40 text-gray-100'
  },
  5: {
    label: 'The Void',
    color: 'bg-gray-800/50 text-white'
  }
};
import { abbreviateGenre } from '../../utils/genreAbbreviations';
const UnifiedBookCard: React.FC<UnifiedBookCardProps> = ({
  book,
  series,
  darkMode = false,
  isCollaboration = false,
  isSigned = false
}) => {
  const [imgError, setImgError] = useState(false);
  const {
    isAfterTide
  } = usePlatform();
  const {
    preferences
  } = useAfterTideUser();
  const isSeries = !!series;
  // Determine which book to display
  // For series, we show the first book's cover
  const displayBook = isSeries ? series.books[0] : book;
  if (!displayBook) return null;
  // Helper to safely coerce a value to string (returns fallback if not a string)
  const safeString = (val: any, fallback: string = ''): string => typeof val === 'string' ? val : fallback;
  // Normalize the book data to handle different schemas
  const normalizedBook = {
    id: displayBook.id,
    title: safeString(displayBook.title, 'Unknown Title'),
    author: safeString(displayBook.author, 'Unknown Author'),
    coverImage: safeString(displayBook.coverImage),
    rating: displayBook.rating || 0,
    reviewCount: displayBook.reviewCount || 0,
    genres: (displayBook.genres || (displayBook.genre ? [displayBook.genre] : [])).filter((g: any) => typeof g === 'string'),
    subgenres: (displayBook.subgenres || (displayBook.subgenre ? [displayBook.subgenre] : [])).filter((s: any) => typeof s === 'string'),
    spiceLevel: displayBook.spiceLevel || 0,
    burnLevel: displayBook.burnLevel || 0,
    darknessLevel: displayBook.darknessLevel || 0,
    endingType: safeString(displayBook.endingType),
    relationshipType: safeString(displayBook.relationshipType),
    contentWarnings: (displayBook.contentWarnings || []).filter((w: any) => typeof w === 'string'),
    seriesName: isSeries ? safeString(series.name) : safeString(displayBook.seriesName) || safeString(displayBook.series) || '',
    seriesStatus: isSeries ? safeString(series.status) : safeString(displayBook.seriesStatus) || undefined,
    seriesNumber: displayBook.seriesNumber || 0,
    totalSeriesBooks: isSeries ? series.totalBooks : displayBook.totalSeriesBooks || 0,
    formats: Array.isArray(displayBook.formats) ? displayBook.formats.filter((f: any) => typeof f === 'string') : [],
    isNew: isSeries ? series.isNew : displayBook.isNew || false,
    isDebut: isSeries ? series.isDebut : displayBook.isDebut || false,
    coAuthor: safeString(displayBook.coAuthor),
    consentFramework: safeString(displayBook.consentFramework),
    tropes: (displayBook.tropes || []).filter((t: any) => typeof t === 'string'),
    pairingType: safeString(displayBook.pairingType),
    primaryGenre: safeString(displayBook.primaryGenre),
    mainSubgenre: safeString(displayBook.mainSubgenre),
    secondaryGenres: (displayBook.secondaryGenres || []).filter((g: any) => typeof g === 'string'),
    subgenreType: displayBook.subgenreType,
    displayLabel: safeString(displayBook.displayLabel),
    shortLabel: safeString(displayBook.shortLabel),
    refinements: (displayBook.refinements || []).filter((r: any) => typeof r === 'string'),
    isAnthology: !!(displayBook as any).isAnthology
  };
  // Linked editions. Derived from the shared registry rather than a prop, so
  // every surface showing this card agrees on the edition count without each
  // caller having to remember to pass it.
  const linkedStory = isSeries ? undefined : getLinkedStoryForBook(normalizedBook.id);
  // Check if the series name is longer than "Elements Trilogy" (16 characters)
  const isLongSeriesName = normalizedBook.seriesName && normalizedBook.seriesName.length > 16;
  // Determine if the book is part of a romance genre for conditional rendering
  const isRomance = normalizedBook.genres.some(genre => typeof genre === 'string' && genre.toLowerCase().includes('romance'));
  // Count how many tag sections we have to determine spacing
  const hasContentWarnings = normalizedBook.contentWarnings && normalizedBook.contentWarnings.length > 0;
  // Determine if we should show the CW icon on the cover
  const showCWIcon = isAfterTide ? hasContentWarnings && normalizedBook.contentWarnings.some(w => preferences.excludedWarnings.includes(w)) : hasContentWarnings;
  // Link to the appropriate detail page based on platform
  const getDetailLink = () => {
    if (isSeries) {
      return isAfterTide ? `/aftertide/series/${series?.id}` : `/series/${series?.id}`;
    }
    if (normalizedBook.isAnthology) {
      return `/anthology/${normalizedBook.id}`;
    }
    return isAfterTide ? `/aftertide/book/${normalizedBook.id}` : `/book/${normalizedBook.id}`;
  };
  const getAuthorLink = (authorName: string) => {
    if (!authorName || typeof authorName !== 'string') return '#';
    const slug = authorName.toLowerCase().replace(/\s+/g, '-');
    return isAfterTide ? `/aftertide/author/${slug}` : `/author/${slug}`;
  };
  const getSeriesLink = (seriesName: string) => {
    if (!seriesName || typeof seriesName !== 'string') return '#';
    const slug = seriesName.toLowerCase().replace(/\s+/g, '-');
    return isAfterTide ? `/aftertide/series/${slug}` : `/series/${slug}`;
  };
  const detailLink = getDetailLink();
  // Link to the book's reviews page
  const reviewsLink = isAfterTide ? `/aftertide/book/${normalizedBook.id}/reviews` : `/book/${normalizedBook.id}/reviews`;
  // Determine status label text
  const getStatusLabel = () => {
    if (normalizedBook.isDebut) return 'DEBUT';
    if (normalizedBook.isNew) return 'NEW';
    return normalizedBook.seriesStatus?.charAt(0).toUpperCase() + normalizedBook.seriesStatus?.slice(1);
  };
  // Extract co-author from the author string if this is a collaboration
  const getCoAuthor = () => {
    if (normalizedBook.coAuthor) return normalizedBook.coAuthor;
    if (isCollaboration && normalizedBook.author.includes('&')) {
      const authors = normalizedBook.author.split('&');
      return authors[1].trim();
    }
    return '';
  };
  const coAuthor = getCoAuthor();
  // Platform-aware tag shape: AfterTide = sharp/rectangular, LitLaunch = soft pills
  const tagRadius = isAfterTide ? 'rounded-sm' : 'rounded-full';
  return <div className="relative group/card w-full" data-id="element-1052" data-mp="1agjel9cshm80">
      {/* Book Cover */}
      <Link to={detailLink} className={`block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-all duration-300 ${isAfterTide ? 'border border-transparent group-hover/card:border-[#e2b04a]/50 group-hover/card:shadow-[0_0_15px_rgba(226,176,74,0.15)]' : 'group-hover/card:scale-105'}`} data-id="element-1053" data-mp="1f9w3zlpalx1t">
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-1054" data-mp="f6o98n7jztpb">
            <BookIcon className="h-10 w-10 text-indigo-400 mb-2" data-id="element-1055" data-mp="59tbi6j3txc3" />
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-1056" data-mp="c07nk59xfez">
              {normalizedBook.title}
            </span>
          </div> : <img src={normalizedBook.coverImage} alt={isSeries ? normalizedBook.seriesName : normalizedBook.title} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-1057" data-mp="3shdik4ey3wkj" />}
        {/* Genre/Subgenre tag top-left with solid color */}
        {(() => {
        // Use structured displayLabel if available, otherwise fall back to old logic
        if (normalizedBook.displayLabel) {
          const colorGenre = normalizedBook.primaryGenre || normalizedBook.displayLabel;
          return <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(colorGenre)} px-2 py-0.5 ${tagRadius}`} data-id="element-1058" data-mp="2auiip2xyh9c">
                {abbreviateGenre(normalizedBook.displayLabel)}
              </span>;
        }
        // Legacy fallback
        // AfterTide: show subgenre (more useful for 18+ audience who already know the main genre)
        // LitLaunch: show main genre
        const tagLabel = isAfterTide ? normalizedBook.subgenres[0] || normalizedBook.genres[0] : normalizedBook.genres[0];
        if (!tagLabel) return null;
        return <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(tagLabel)} px-2 py-0.5 ${tagRadius}`} data-id="element-1059" data-mp="35rjpudaclv02">
              {abbreviateGenre(tagLabel)}
            </span>;
      })()}

        {/* Content Warning icon indicator - LitLaunch only (AfterTide audience already knows) */}
        {!isAfterTide && showCWIcon && <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md z-10" title={`Content Warnings: ${normalizedBook.contentWarnings.join(', ')}`} data-id="element-1060" data-mp="2kh5irni1be3b">
            <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-1061" data-mp="385g9hxu0nxdv" />
          </span>}
        {/* Linked editions badge — bottom right, clear of the genre tag
            (top-left), status badge (top-right) and CW icon (bottom-left).
            Absent for single-edition books, which look unchanged. */}
        {linkedStory && <span className={`absolute bottom-2 right-2 inline-flex items-center gap-1 text-white text-[10px] font-medium bg-black/70 px-1.5 py-0.5 ${tagRadius} z-10`} title={`${linkedStory.editions.length} linked editions of this story`} data-id="element-1062" data-mp="3c8cx62yeyjoo">
            <LayersIcon className="h-3 w-3" data-id="element-1063" data-mp="3hvs0s2j0n5oc" />
            {linkedStory.editions.length} Editions
          </span>}
        {/* Status badge (series status, new, or debut) - top right */}
        {(normalizedBook.seriesStatus || normalizedBook.isNew || normalizedBook.isDebut) && <span className={`absolute top-2 right-2 text-white text-xs ${getStatusColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} px-2 py-0.5 ${tagRadius}`} data-id="element-1064" data-mp="3vvt3zomr6p6z">
            {getStatusLabel()}
          </span>}
        {/* Hover Overlay */}
        <div className={`absolute inset-0 ${isAfterTide ? 'bg-[#1a1a2e]/90' : 'bg-black/85'} backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover/card:opacity-100`} data-id="element-1065" data-mp="eejkvo7umfw9">
          {/* Top section with title/series, author info */}
          <div className="min-w-0" data-id="element-1066" data-mp="sdt12nw21a08">
            {isSeries ?
          // Series display
          <>
                <div className="flex items-center justify-between mb-0.5" data-id="element-1067" data-mp="2cxobnx8ki2vk">
                  <Link to={getSeriesLink(normalizedBook.seriesName)} className={`font-bold text-white text-sm line-clamp-1 transition-colors ${isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-indigo-300'}`} onClick={e => e.stopPropagation()} data-id="element-1068" data-mp="27x0nrkfmy25k">
                    {normalizedBook.seriesName}
                  </Link>
                  {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && !isLongSeriesName && <span className={`ml-2 flex-shrink-0 text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded`} data-id="element-1069" data-mp="2msonawa0e134">
                        {normalizedBook.seriesNumber}/
                        {normalizedBook.totalSeriesBooks}
                      </span>}
                </div>
                {/* Series number on next line for long names */}
                {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && isLongSeriesName && <span className={`inline-block text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded mb-1`} data-id="element-1070" data-mp="2dwxxjciilver">
                      Book {normalizedBook.seriesNumber}/
                      {normalizedBook.totalSeriesBooks}
                    </span>}
              </> :
          // Standalone book display
          <>
                <div className="flex items-center justify-between mb-0.5" data-id="element-1071" data-mp="1h773tfi1sca0">
                  <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-1072" data-mp="1l8sgazqo6ojo">
                    {normalizedBook.title}
                  </h3>
                  {normalizedBook.seriesName && normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && !isLongSeriesName && <span className={`ml-2 flex-shrink-0 text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded`} data-id="element-1073" data-mp="1nn72fynlg1du">
                        {normalizedBook.seriesNumber}/
                        {normalizedBook.totalSeriesBooks}
                      </span>}
                </div>
                {/* Series number on next line for long names */}
                {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && isLongSeriesName && <span className={`inline-block text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded mb-1`} data-id="element-1074" data-mp="79z0g55tilk1">
                      Book {normalizedBook.seriesNumber}/
                      {normalizedBook.totalSeriesBooks}
                    </span>}
              </>}

            {/* Author */}
            <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-1075" data-mp="1tg1y12wea6o0">
              by{' '}
              <Link to={getAuthorLink(normalizedBook.author)} className={`hover:underline transition-colors ${isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-indigo-300'}`} onClick={e => e.stopPropagation()} data-id="element-1076" data-mp="2857y0t3240wy">
                {normalizedBook.author}
              </Link>
            </p>

            {/* Co-author for collaborations */}
            {isCollaboration && coAuthor && <p className="text-white/80 text-xs line-clamp-1 mb-1.5 -mt-1" data-id="element-1077" data-mp="ismmy812jyiy">
                with{' '}
                <Link to={getAuthorLink(coAuthor)} className={`hover:underline transition-colors ${isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-indigo-300'}`} onClick={e => e.stopPropagation()} data-id="element-1078" data-mp="8nxt1jfqd62d">
                  {coAuthor}
                </Link>
              </p>}

            {/* Series name for standalone books */}
            {!isSeries && normalizedBook.seriesName && <div className={`text-[11px] flex items-center mb-1.5 ${isAfterTide ? 'text-[#e2b04a]/80' : 'text-indigo-300'}`} data-id="element-1079" data-mp="3q4o2eqf51xa5">
                <BookOpenIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-1080" data-mp="3c6hcbwgfl1l1" />
                <Link to={getSeriesLink(normalizedBook.seriesName)} className={`line-clamp-1 transition-colors ${isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-indigo-200'}`} onClick={e => e.stopPropagation()} data-id="element-1081" data-mp="3h37s0iobpv5x">
                  {normalizedBook.seriesName}
                </Link>
              </div>}

            {/* Edition picker — lets a reader go straight to the version they
                want instead of landing on the default and switching. Each
                links to the same book detail page with its edition selected,
                never to a separate book record. */}
            {linkedStory && <div className="mb-1.5" data-id="element-1082" data-mp="1wkx82nv08136">
                <span className="block text-[9px] uppercase tracking-wide text-gray-400 mb-1" data-id="element-1083" data-mp="1wo1gjxfi5izn">
                  Choose edition
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-1084" data-mp="1mvdqdlj12d9w">
                  {linkedStory.editions.map(edition => <Link key={edition.id} to={`${detailLink}?edition=${edition.id}`} onClick={e => e.stopPropagation()} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] font-medium bg-white/15 text-white hover:bg-white/30 transition-colors`} data-id="element-1085" data-mp="1hw9g0rkda9hz">
                      {edition.name}
                    </Link>)}
                </div>
              </div>}

            {/* Row 1: Identity Tags (Pairing + Tropes, or Subgenres fallback) */}
            <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-1086" data-mp="2wuf0sc9s8q8e">
              {/* Signed badge */}
              {isSigned && <span className={`px-1.5 py-0.5 bg-amber-500/80 text-white ${tagRadius} text-[10px] font-medium`} data-id="element-1087" data-mp="30v7cdc64n77u">
                  ✍ Signed
                </span>}

              {/* Identity Tags Logic */}
              {(() => {
              const hasIdentityTags = normalizedBook.pairingType || normalizedBook.tropes && normalizedBook.tropes.length > 0;
              if (hasIdentityTags) {
                const tags = [];
                if (normalizedBook.pairingType) {
                  tags.push({
                    text: normalizedBook.pairingType,
                    type: 'pairing'
                  });
                }
                if (normalizedBook.tropes) {
                  normalizedBook.tropes.slice(0, 2).forEach(trope => {
                    tags.push({
                      text: trope,
                      type: 'trope'
                    });
                  });
                }
                return tags.map((tag, index) => <span key={`id-tag-${index}`} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] ${isAfterTide ? 'bg-[#1a1a2e] border border-[#e2b04a]/30 text-[#e2b04a]' : tag.type === 'pairing' ? 'bg-pink-500/80 text-white' : `${getContentTagColor(tag.text)}/80 text-white`}`} data-id="element-1088" data-mp="28ffttylgulc" data-mpi={index}>
                      {tag.text}
                    </span>);
              } else if (normalizedBook.subgenres && normalizedBook.subgenres.length > 0) {
                // Fallback to subgenres
                return normalizedBook.subgenres.slice(0, 3).map((subgenre, index) => <span key={`subgenre-${index}`} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] ${isAfterTide ? 'bg-[#1a1a2e] border border-[#e2b04a]/30 text-[#e2b04a]' : `${getGenreColor(subgenre)}/80 text-white`}`} data-id="element-1089" data-mp="1q4dj0rp12z3d" data-mpi={index}>
                        {abbreviateGenre(subgenre)}
                      </span>);
              }
              return null;
            })()}
            </div>

            {/* Content Warnings */}
            {hasContentWarnings && <div className={`flex items-center mb-1.5 ${isAfterTide ? 'flex-wrap gap-1' : ''}`} data-id="element-1090" data-mp="2wrhdqlz0xt2r">
                {isAfterTide ?
            // AfterTide: Show warnings as text tags
            <>
                    {normalizedBook.contentWarnings.slice(0, 3).map((warning, index) => <span key={`cw-${index}`} className={`px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 ${tagRadius} text-[9px]`} data-id="element-1091" data-mp="31ga7ycn0s5iy" data-mpi={index}>
                          {warning}
                        </span>)}
                    {normalizedBook.contentWarnings.length > 3 && <span className={`px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 ${tagRadius} text-[9px]`} data-id="element-1092" data-mp="2irvezjt56hfi">
                        +{normalizedBook.contentWarnings.length - 3}
                      </span>}
                  </> :
            // LitLaunch: Show icon
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${normalizedBook.contentWarnings.join(', ')}`} data-id="element-1093" data-mp="ka316v7o6jwj">
                    <AlertCircleIcon className="h-3 w-3" data-id="element-1094" data-mp="t85cq4p14uy2" />
                  </span>}
              </div>}

            {/* Row 2: Content Levels (Spice, Burn, Darkness, Ending) */}
            <div className="mt-1 flex flex-wrap gap-1" data-id="element-1095" data-mp="5kdny1maypfw">
              {normalizedBook.spiceLevel > 0 && spiceLevelMap[normalizedBook.spiceLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${spiceLevelMap[normalizedBook.spiceLevel].color}`} data-id="element-1096" data-mp="ai5qdpkkptyc">
                    {spiceLevelMap[normalizedBook.spiceLevel].label}
                  </span>}

              {normalizedBook.burnLevel > 0 && burnLevelMap[normalizedBook.burnLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${burnLevelMap[normalizedBook.burnLevel].color}`} data-id="element-1097" data-mp="27cuza9wl8s4m">
                    {burnLevelMap[normalizedBook.burnLevel].label}
                  </span>}

              {!isAfterTide && normalizedBook.darknessLevel > 0 && darknessLevelMap[normalizedBook.darknessLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${darknessLevelMap[normalizedBook.darknessLevel].color}`} data-id="element-1098" data-mp="2qb7jcqp1fmez">
                    {darknessLevelMap[normalizedBook.darknessLevel].label}
                  </span>}

              {normalizedBook.endingType && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${normalizedBook.endingType === 'HEA' || normalizedBook.endingType === 'HFN' ? 'bg-green-600/30 text-green-200' : 'bg-red-500/30 text-red-200'}`} data-id="element-1099" data-mp="3939u5lf008hb">
                  {normalizedBook.endingType === 'HEA' || normalizedBook.endingType === 'HFN' ? `✓ ${normalizedBook.endingType}` : '✕ No HEA'}
                </span>}
            </div>
          </div>

          {/* Bottom section - Rating & Formats */}
          <div className="mt-auto flex items-center justify-between" data-id="element-1100" data-mp="3ep0x1q155gf5">
            <Link to={reviewsLink} className={`flex items-center gap-1 transition-colors ${isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-indigo-300'}`} onClick={e => e.stopPropagation()} data-id="element-1101" data-mp="2zzuxu22azq79">
              <StarIcon className={`h-4 w-4 ${normalizedBook.isDebut && !isAfterTide ? 'text-fuchsia-400' : 'text-amber-400'}`} data-id="element-1102" data-mp="2r0rh34qlzmvx" />

              <span className="text-[11px]" data-id="element-1103" data-mp="1iwrvlqljdztp">
                {normalizedBook.rating > 0 ? normalizedBook.rating : 'New'}
              </span>
              {normalizedBook.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-1104" data-mp="1rw52cbia0aaq">
                  ({normalizedBook.reviewCount})
                </span>}
            </Link>
            {/* Format icons */}
            {normalizedBook.formats && normalizedBook.formats.length > 0 && <div className="flex items-center gap-1.5" data-id="element-1105" data-mp="25vxpxxyiaaeh">
                {normalizedBook.formats.includes('ebook') && <div className="bg-white/15 rounded-full p-1" title="eBook" data-id="element-1106" data-mp="2anxoblyxgv8y">
                    <TabletIcon className="h-3 w-3 text-white" data-id="element-1107" data-mp="2fmim7eqpj2u8" />
                  </div>}
                {normalizedBook.formats.includes('paperback') && <div className="bg-white/15 rounded-full p-1" title="Paperback" data-id="element-1108" data-mp="2pk6jocfp6og3">
                    <BookOpenIcon className="h-3 w-3 text-white" data-id="element-1109" data-mp="dxuw8acx7pe1" />
                  </div>}
                {normalizedBook.formats.includes('hardcover') && <div className="bg-white/15 rounded-full p-1" title="Hardcover" data-id="element-1110" data-mp="3y41q8od1ady">
                    <BookTextIcon className="h-3 w-3 text-white" data-id="element-1111" data-mp="3v6hyn5h7eyhx" />
                  </div>}
                {normalizedBook.formats.includes('audiobook') && <div className="bg-white/15 rounded-full p-1" title="Audiobook" data-id="element-1112" data-mp="rp9gekikdwmo">
                    <HeadphonesIcon className="h-3 w-3 text-white" data-id="element-1113" data-mp="mveztpy99xrt" />
                  </div>}
              </div>}
          </div>
        </div>
      </Link>
    </div>;
};
export default UnifiedBookCard;